/**
 * M3X.WORKSPACE-CLOSURE Session 2 PR 9 — first-session keyboard
 * shortcut hint card. Auto-dismisses after 8s; manual dismiss via X.
 * Persists `solvait.workspace.kbdHintSeen.{clerkUserId}` so it only
 * shows once per user.
 */

import { Sparkles, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";


function _seenKey(userId: string | null): string {
  return `solvait.workspace.kbdHintSeen.${userId ?? "anon"}`;
}


export interface KbdHintCardProps {
  userId: string | null;
}


export function KbdHintCard({ userId }: KbdHintCardProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.localStorage.getItem(_seenKey(userId)) !== "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => _dismiss(), 8_000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  function _dismiss() {
    setVisible(false);
    try {
      window.localStorage.setItem(_seenKey(userId), "1");
    } catch {
      // ignore
    }
  }

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-kbd-hint-card
      // pointer-events-none lets clicks fall through to underlying UI —
      // the card is purely informational. The dismiss button gets
      // pointer-events-auto so the X is still clickable. Without this,
      // any click target the card overlaps (e.g. a wizard's Next button
      // in the bottom-right corner) is silently intercepted until the
      // card auto-dismisses, which is both UX-bad AND racy under
      // Playwright (step 23 of UI_EMP_001 caught it).
      className="pointer-events-none fixed bottom-6 end-6 z-40 flex max-w-[280px] items-start gap-3"
      style={{
        backgroundColor: "white",
        border: "1px solid #e2e8f0",
        borderRadius: "10px",
        padding: "12px 14px",
        fontSize: "12px",
        color: "#475569",
        boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
      }}
    >
      <Sparkles className="h-3.5 w-3.5 flex-shrink-0" color="#00D3C2" />
      <div className="flex-1 leading-relaxed">
        Press <Kbd>J</Kbd> <Kbd>K</Kbd> <Kbd>H</Kbd> <Kbd>L</Kbd> to
        navigate · <Kbd>⌘1-7</Kbd> to jump modules · <Kbd>[</Kbd> to
        cycle the rail
        <span className="sr-only">
          {t("workspace.kbd.hint.cycleRail2", "Press [ to cycle the rail")}
        </span>
      </div>
      <button
        type="button"
        onClick={_dismiss}
        aria-label="Dismiss"
        className="pointer-events-auto -mt-0.5 cursor-pointer rounded p-0.5 hover:bg-surface-subtle"
        style={{ color: "#94a3b8" }}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}


function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd
      style={{
        display: "inline-block",
        backgroundColor: "#f1f5f9",
        border: "1px solid #e2e8f0",
        padding: "1px 5px",
        borderRadius: "3px",
        fontSize: "10px",
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        margin: "0 1px",
      }}
    >
      {children}
    </kbd>
  );
}


export default KbdHintCard;
